import {
  Dialog,
  DialogContent,
  Grid,
  autocompleteClasses,
} from '@mui/material';
import { Box } from '@mui/system';
import MediumTypography from '../../components/formlib/MediumTypography';
import Textinput from '../../components/formlib/Textinput';
import { DATE_FORMAT, employeeTypes } from '../../services/Constant';
import SelectComponent from '../../components/formlib/SelectComponent';
import AutocompleteSearch from '../../components/formlib/AutocompleteSearch';
import { OptionType, employeeRoles } from '../../utils/type';
import MultipleAutocompleteSearch from '../../components/formlib/MultipleAutocompleteSearch';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { EmployeeFormData, Program } from './types';
import { Formik, FormikErrors, FormikProps } from 'formik';
import * as yup from 'yup';
import { useNavigate } from 'react-router';

import { ReactComponent as Cross } from '../../assets/images/x.svg';
import { EMPLOYEES_ROUTE_NAME } from '../../routes/Routing';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useRef, useState } from 'react';
import { sanitizeDecimalInput } from '../../utils/utilities';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';
import { EmployeeDisciplineHistory } from '../../services/configApi/employees/employeeServices';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import ActionToolTip from '../../components/formlib/ActionToolTip';
import { checkPermissionForFeature } from '../../utils/checkPermission';

interface OnBoardingEmployeeModalProps {
  isSuperAdmin:boolean
  modalVisible: boolean;
  employeeData: EmployeeFormData;
  employeeList: OptionType[];
  groups: OptionType[];
  handleSubmission: (values: EmployeeFormData) => void;
}

export const staffTypes: OptionType[] = [
  {
    id: 'CLINICAL',
    label: 'Clinical',
  },
  {
    id: 'CLERICAL',
    label: 'Clerical',
  },
  {
    id: 'ADMINISTRATION',
    label: 'Administration',
  },
];
export interface GroupType {
  id: string;
  groupName: string;
  createdBy?: string;
  level?: number;
}

const validateForm = (value: EmployeeFormData) => {
  const errors: Partial<EmployeeFormData> = {};

  if (value.employeeStartDate) {
    if (datePickerMinDateMaxDateValidate(value.employeeStartDate)) {
      errors.employeeStartDate = 'datePickerMinDateMaxDateValidate';
    }
  }

  if (value.employeeEndDate) {
    if (datePickerMinDateMaxDateValidate(value.employeeEndDate)) {
      errors.employeeEndDate = 'datePickerMinDateMaxDateValidate';
    }
  }

  if (value.employeeCredentials.credentialExpirationDate) {
    if (
      datePickerMinDateMaxDateValidate(
        value.employeeCredentials.credentialExpirationDate,
      )
    ) {
      errors.employeeCredentials = {
        credentialExpirationDate: 'datePickerMinDateMaxDateValidate',
        credentialFileUrl: '',
        credentialLicenseNumber: '',
      };
    }
  }

  return errors;
};

const validationSchema = yup.object().shape({
  employeeStartDate: yup.string().required('joiningDate.fieldEmptyMessage'),
  employeeEndDate: yup.string().nullable(),
  isRehireChecked: yup.boolean().nullable(),
  rehireMonths: yup.string().nullable().when('isRehireChecked', {
  is: true,
  then: (schema) => schema.required('Feild is required'),
}),


  workingHours: yup
    .string()
    .required('CommunicationLogPage.fieldEmptyMessage')
    .test(
      'valid-hours-starting-balance',
      'workingHoursInValidErrorMessage',
      (value) => {
        if (!value) return false;

        let v = value.trim();

        v = v.replace(':', '.');
        if (v.startsWith('.')) v = `00${v}`;
        if (/^\d+\.$/.test(v)) v = v.replace('.', '.00');
        if (/^\d+$/.test(v)) v = v.padStart(2, '0') + '.00';

        const match = v.match(/^(\d{1,3})\.(\d{1,2})$/);
        if (!match) return false;

        const [, intRaw, decRaw] = match;

        const hours = Number(intRaw);
        let minutes = decRaw;

        if (hours > 99) return false;

        const allowed = ['00', '25', '50', '75'];

        if (!allowed.includes(minutes)) {
          if (minutes.length === 1) {
            minutes = minutes + '0'; // ".5" → ".50"
            if (!allowed.includes(minutes)) return false;
          } else {
            return false;
          }
        }

        const finalValue = Number(`${hours}.${minutes}`);

        if (finalValue > 120.0) return false;

        return true;
      },
    ),
  reportingTo: yup
    .object()
    .shape({
      id: yup.string(),
      label: yup.string(),
    })
    .required('CommunicationLogPage.fieldEmptyMessage')
    .nullable(),
  employeeRole: yup.string().required('employeeRole.fieldEmptyMessage'),
  employeeType: yup.string().required('CommunicationLogPage.fieldEmptyMessage'),
  lunchWaiverRequired: yup.boolean().nullable(),
  lunchWaiverStartDate: yup.string().nullable(),
  group: yup
    .object()
    .shape({
      id: yup.string().required('CommunicationLogPage.fieldEmptyMessage'),
      groupName: yup
        .string()
        .required('CommunicationLogPage.fieldEmptyMessage'),
    })
    .nullable(),
  productivityGoal: yup
    .number()
    .min(0, 'minAllowedProductivityGoalErrorMessage')
    .max(100.0, 'maxAllowedProductivityGoalErrorMessage')
    .nullable(),
  programs: yup.array().of(
    yup.object().shape({
      staffType: yup
        .string()
        .required('CommunicationLogPage.fieldEmptyMessage'),
    }),
  ),
  disciplineHistoryList: yup
    .array()
    .of(
      yup.object().shape({
        disciplineType: yup.string().required('discipline.fieldEmptyMessage'),
        startDate: yup.string().when('disciplineType', {
          is: 'NA',
          then: (schema) => schema.nullable(),
          otherwise: (schema) =>
            schema.required('CommunicationLogPage.fieldEmptyMessage'),
        }),
      }),
    )
    .min(1, 'discipline.fieldEmptyMessage'),
  npi: yup.string().when('disciplineHistoryList', (list, schema) => {
    const hasNonNA = Array.isArray(list)
      ? list.some(
          (item) => item && item.disciplineType && item.disciplineType !== 'NA',
        )
      : false;
    return hasNonNA
      ? schema.min(10, 'tenDigitNpiValidation').required('npiNumberValidation')
      : schema.nullable();
  }),
});

const OnBoardingEmployeeModal = ({
  isSuperAdmin,
  modalVisible,
  employeeData,
  employeeList,
  groups,
  handleSubmission,
}: OnBoardingEmployeeModalProps) => {

  const [disciplineList, setDisciplineList] = useState<OptionType[]>([]);
  const [localSelectedProgramId, setLocalSelectedProgramId] =
    useState<string>('');
  const hasTierRangeAdjustmentEditPermission = checkPermissionForFeature(
    'backend.tier_range_adjustments',
    'editPermission',
  );
console.log("hasTierRangeAdjustmentEditPermission",hasTierRangeAdjustmentEditPermission)
  const navigate = useNavigate();
  const { lookups } = useContext(LookUpContext);

  useEffect(() => {
    const selectedProgram = localStorage.getItem('selectedProgram');

    if (selectedProgram) {
      const parsed = JSON.parse(selectedProgram);
      setLocalSelectedProgramId(parsed.id);
    }
  }, []);

  useEffect(() => {
    if (lookups === null) {
      return;
    }

    setDisciplineList(() => {
      return lookups.employeeDiscipline?.map((e) => {
        return {
          label: e.description,
          id: e.code,
        };
      });
    });
  }, [lookups]);

  const licenseNumValidation = (value: string): string | undefined => {
    const trimmedValue = value?.replace(/^\s+/, '');
    const regex = /^[A-Za-z0-9!,'. ]*$/;
    if (regex.test(trimmedValue)) {
      return trimmedValue;
    }
  };

  const handleOnBoardSubmit = (values: EmployeeFormData) => {
    const inputDate = dayjs(values.employeeStartDate);

    // 0 = Sunday (Day.js uses Sunday as 0)
    const finalSunday =
      inputDate.day() === 0
        ? inputDate
        : inputDate.subtract(inputDate.day(), 'day');

    const formattedDate = finalSunday.format(DATE_FORMAT);

    handleSubmission({
      ...values,
      productivityGoalStartDate: formattedDate,
      employeeTypeStartDate: formattedDate,
      workingHoursPerWeekStartDate: formattedDate,
      lunchWaiverStartDate: formattedDate,
      employeeStartDate: dayjs(values.employeeStartDate).format(DATE_FORMAT),
      employeeEndDate:
        values.employeeEndDate &&
        dayjs(values.employeeEndDate).format(DATE_FORMAT),
      disciplineHistoryList: [
        {
          fId: '',
          id: values.disciplineHistoryList?.[0]?.id ?? null,
          disciplineType:
            values.disciplineHistoryList?.[0]?.disciplineType ?? null,
          credentialLicenseNumber:
            values.disciplineHistoryList?.[0]?.credentialLicenseNumber ?? null,
          credentialExpirationDate:
            values.disciplineHistoryList?.[0]?.credentialExpirationDate ?? null,
          credentialFileUrl: '',
          startDate: dayjs(values.employeeStartDate).format('YYYY-MM-DD'),
          isChanged: true,
          isDeleted: false,
        },
      ],
    });
  };
  const formikRef = useRef<FormikProps<EmployeeFormData>>(null);
  return (
    <Box component="div">
      <Formik
        innerRef={formikRef}
        initialValues={{
          ...employeeData,
          employeeStartDate: employeeData.employeeStartDate
            ? dayjs(employeeData.employeeStartDate)
            : dayjs().format(DATE_FORMAT),
          employeeEndDate: employeeData.employeeEndDate
            ? dayjs(employeeData.employeeEndDate)
            : null,
        }}
        enableReinitialize
        validateOnChange
        validationSchema={validationSchema}
        validate={validateForm}
        onSubmit={(values) => {
          handleOnBoardSubmit(values);
        }}
      >
        {({ values, setFieldValue, errors, touched, handleSubmit }) => (
          <Dialog
            open={modalVisible}
            keepMounted
            onClose={() => ({})}
            fullWidth
            aria-describedby="alert-dialog-slide-description"
            maxWidth={'lg'}
          >
            <DialogContent sx={{ padding: '16px 24px' }}>
              <Box className="flex__ alignItemCenter justifyContentSpaceBtw">
                <MediumTypography
                  labelid={'onBoardingModalTitle'}
                  defaultlabel="Onboarding"
                  fontSize="24px"
                  fontweight={600}
                  textColor="#2a4241"
                />
                <Cross
                  style={{
                    cursor: 'pointer',
                    marginLeft: '10px',
                  }}
                  onClick={() => navigate(EMPLOYEES_ROUTE_NAME)}
                />
              </Box>
              <Box
                className="formCardview pl-sm pr-sm pt-lg pb-lg mt-md gray3 borderRadius4"
                sx={{
                  border: '1px solid #00C6B8',
                  background: '#ECF9F8',
                }}
              >
                <Grid container spacing={3} className="flex__">
                  <Grid item xs={4}>
                    <Textinput
                      name="firstName"
                      labelid="AddNewReferralDetails.First"
                      defaultlabelid="First Name"
                      Value={values.firstName}
                      inputProps={{ readOnly: true }}
                      className="bg__white"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Textinput
                      name="middleName"
                      labelid="Clientpage.Middle"
                      defaultlabelid="Middle Name"
                      Value={values.middleName}
                      inputProps={{ readOnly: true }}
                      disabled={true}
                      className="bg__white"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Textinput
                      name="lastName"
                      labelid="AddNewReferralDetails.Last"
                      defaultlabelid="Last Name"
                      Value={values.lastName}
                      inputProps={{ readOnly: true }}
                      className="bg__white"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3} className="flex__ mt-xs">
                  <Grid item xs={4}>
                    <Textinput
                      name="email"
                      labelid="Contacts.email"
                      defaultlabelid="Email"
                      Value={values.email}
                      inputProps={{ readOnly: true }}
                      className="bg__white"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SelectComponent
                      name="employeeType"
                      labelid="employeeTypeText"
                      defaultlabelid="Employee Type"
                      names={employeeTypes}
                      value={values.employeeType ?? ''}
                      handleChange={(e) => {
                        if (e === 'Salaried Exempt') {
                          setFieldValue('lunchWaiverRequired', false);
                        }
                        setFieldValue('employeeType', e);
                      }}
                      Required={true}
                    />
                    {errors.employeeType && touched.employeeType && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.employeeType}
                        defaultlabel="Field should not be empty"
                      />
                    )}

                    {values.employeeType !== 'Salaried Exempt' && (
                      <CheckBoxComponent
                        labelid="lunchWaiverText"
                        defaultlabelid="Lunch Waiver"
                        ischecked={values.lunchWaiverRequired}
                        CheckHandleChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFieldValue(
                            'lunchWaiverRequired',
                            event.target.checked,
                          );
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Textinput
                      name="workingHoursPerWeek"
                      labelid="workingHoursPerWeek"
                      defaultlabelid="Working Hours Per Week"
                      inputProps={{ maxLength: 6 }} // supports "120.75"
                      Value={values.workingHours?.toString()}
                      handlechange={(text) => {
                        const clean = sanitizeDecimalInput(text);
                        if (clean === '') {
                          setFieldValue('workingHours', '');
                          return;
                        }

                        const parts = clean.split('.');
                        const intPart = parts[0] ?? '';
                        const decPart = parts[1] ?? '';

                        if (intPart.length > 3) return;

                        if (decPart.length > 2) return;
                        if (clean === '.') return;
                        if (clean.split('.').length > 2) return;
                        setFieldValue('workingHours', clean);
                      }}
                      className="bg__white"
                      Required
                    />
                    {errors.workingHours && touched.workingHours && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.workingHours}
                        defaultlabel="Field should not be empty"
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid container spacing={3} className="flex__ mt-xs">
                  <Grid item xs={4}>
                    <AutocompleteSearch
                      labelId={'reportingToText'}
                      defaultId="Reporting To"
                      Required
                      optionHintId={'noemployeeDropdownTextId'}
                      sxProps={{
                        [`& .${autocompleteClasses.popupIndicator}`]: {
                          transform: 'none',
                        },
                        backgroundColor: 'white',
                      }}
                      data={employeeList}
                      selectedValue={values.reportingTo as OptionType}
                      onSelectItem={(value: OptionType | null) => {
                        if (value) {
                          setFieldValue('reportingTo', value);
                        } else {
                          setFieldValue('reportingTo', '');
                        }
                      }}
                      disableLabel={true}
                    />
                    {errors.reportingTo && touched.reportingTo && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.reportingTo}
                        defaultlabel="Field should not be empty"
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <MultipleAutocompleteSearch
                      labelId="employeeRoleText"
                      defaultId="Employee Role"
                      data={employeeRoles}
                      type="string"
                      required
                      onSelectItem={(value: OptionType[] | null) => {
                        if (value) {
                          const selectedContactIds = value.map((emp) => emp.id);
                          setFieldValue(
                            'employeeRole',
                            selectedContactIds.toString(),
                          );
                        }
                      }}
                      disableLabel={true}
                      optionHintId={'No Employee Role found'}
                      sxProps={{
                        [`& .${autocompleteClasses.popupIndicator}`]: {
                          transform: 'none',
                          marginTop: '2px',
                        },
                        backgroundColor: 'white',
                      }}
                      value={values.employeeRole
                        .split(',') // Split the string by comma
                        .map((emp) => ({
                          id: emp,
                          label:
                            employeeRoles.find((empRole) => empRole.id === emp)
                              ?.label ?? '', // Set label to empty string if not found
                        }))
                        .filter(
                          (option): option is { id: string; label: string } =>
                            option.label !== '', // Filter out options with empty labels
                        )}
                    />
                    {errors.employeeRole && touched.employeeRole && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.employeeRole}
                        defaultlabel="Field should not be empty"
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <AutocompleteSearch
                      labelId={'selectGroupText'}
                      defaultId="Select Group"
                      optionHintId={'noOptionFound'}
                      sxProps={{
                        [`& .${autocompleteClasses.popupIndicator}`]: {
                          transform: 'none',
                        },
                        backgroundColor: 'white',
                      }}
                      data={
                        groups
                        // JSON.parse(
                        //   localStorage.getItem('selectedProgram') || '{}',
                        // ).id === superProgramId
                        //   ? groups.map((group) => ({
                        //       id: group.id,
                        //       label: group.groupName,
                        //     }))
                        //   : groups
                        //       .filter(
                        //         (group) =>
                        //           group.createdBy !== 'Default' ||
                        //           group.level !== 1,
                        //       )
                        //       .map((group) => ({
                        //         id: group.id,
                        //         label: group.groupName,
                        //       }))
                      }
                      selectedValue={{
                        id: values.group.id ?? '',
                        label: values.group.groupName ?? '',
                      }}
                      onSelectItem={(value: OptionType | null) => {
                        if (value) {
                          setFieldValue('group', {
                            id: value.id,
                            groupName: value.label,
                          });
                        } else {
                          setFieldValue('group', { id: '', groupName: '' });
                        }
                      }}
                      disableLabel={true}
                      Required
                    />
                    {errors.group && touched.group && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.group.id}
                        defaultlabel="Field should not be empty"
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Textinput
                      className="bg__white"
                      name="productivityGoalText"
                      labelid="productivityGoalText"
                      defaultlabelid="Productivity Goal(%)"
                      inputProps={{ maxLength: 6 }}
                      Value={values.productivityGoal.toString()}
                      handlechange={(text) => {
                        const formatedString = text
                          .replace(/[^0-9.]/g, '') // remove everything except digits and dot
                          .replace(/^(\d*\.\d{0,2}).*$/, '$1'); // keep only up to 2 decimals
                        setFieldValue('productivityGoal', formatedString);
                      }}
                      onblur={() => {
                        if (values.productivityGoal) {
                          setFieldValue(
                            'productivityGoal',
                            parseFloat(values.productivityGoal).toFixed(2),
                          );
                        }
                      }}
                    />
                    {errors.productivityGoal && touched.productivityGoal && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.productivityGoal}
                        defaultlabel="Field should not be empty"
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <DropdownComponent
                      name="staffType"
                      labelid="staffTypeText"
                      defaultlabelid="Tier Type"
                      names={staffTypes}
                      value={
                        values.programs.find(
                          (item) => item.programId === localSelectedProgramId,
                        )?.staffType || ''
                      }
                      handleChange={(e) => {
                        const updatedPrograms = values.programs.map((item) => {
                          if (item.programId === localSelectedProgramId) {
                            return {
                              ...item,
                              staffType: e,
                            };
                          }
                          return item;
                        });

                        formikRef.current?.setValues({
                          ...values,
                          programs: updatedPrograms,
                        });
                      }}
                      Required={true}
                    />
                    {errors.programs &&
                      errors.programs.length !== 0 &&
                      errors.programs.length >
                        values.programs.findIndex(
                          (item) => item.programId === localSelectedProgramId,
                        ) &&
                      errors.programs[
                        values.programs.findIndex(
                          (item) => item.programId === localSelectedProgramId,
                        )
                      ] &&
                      (
                        errors.programs[
                          values.programs.findIndex(
                            (item) => item.programId === localSelectedProgramId,
                          )
                        ] as FormikErrors<Program>
                      ).staffType &&
                      touched.programs &&
                      touched.programs.length >
                        values.programs.findIndex(
                          (item) => item.programId === localSelectedProgramId,
                        ) &&
                      touched.programs[
                        values.programs.findIndex(
                          (item) => item.programId === localSelectedProgramId,
                        )
                      ]?.staffType && (
                        <MediumTypography
                          className="errorText-md"
                          labelid={
                            'OnBoardingEmployeeModal.staffTypeErrorMessage'
                          }
                          defaultlabel="Tier Type is required."
                        />
                      )}
                  </Grid>
                  <Grid item xs={4}>
                    <DatePickerComponent
                      className="bg__white"
                      labelid="editEmpjoinDateText"
                      defaultlabelid="Join Date"
                      value={dayjs(values.employeeStartDate) as Dayjs}
                      required={true}
                      disableFuture={false}
                      handlechange={(date: Dayjs | null) => {
                        if (date === null) {
                          formikRef.current?.setValues({
                            ...values,
                            employeeStartDate: null,
                            employeeEndDate: null,
                            lunchWaiverStartDate: null,
                          });
                        } else {
                          const formattedDate = dayjs(date).format(DATE_FORMAT);
                          formikRef.current?.setValues({
                            ...values,
                            employeeStartDate: formattedDate,
                            employeeEndDate: null,
                          });
                          if (
                            values.disciplineHistoryList !== undefined &&
                            values.disciplineHistoryList !== null &&
                            values.disciplineHistoryList.length > 0
                          ) {
                            formikRef.current?.setFieldValue(
                              `disciplineHistoryList.${0}.startDate`,
                              formattedDate,
                            );
                          }
                        }
                      }}
                    />
                    {errors.employeeStartDate && touched.employeeStartDate && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.employeeStartDate}
                        defaultlabel="Field should not be empty"
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <DatePickerComponent
                      className="bg__white"
                      labelid="editEmpEndDateText"
                      defaultlabelid="End Date"
                      value={dayjs(values.employeeEndDate) as Dayjs}
                      required={false}
                      disableFuture={false}
                      minDate={dayjs(values.employeeStartDate)}
                      handlechange={(date: Dayjs | null) => {
                        if (date === null) {
                          formikRef.current?.setValues({
                            ...values,
                            employeeEndDate: null,
                          });
                        } else {
                          formikRef.current?.setValues({
                            ...values,
                            employeeEndDate: dayjs(date).format(DATE_FORMAT),
                          });
                        }
                      }}
                    />
                    {errors.employeeEndDate && touched.employeeEndDate && (
                      <MediumTypography
                        className="errorText-md"
                      
                        defaultlabel="Field should not be empty"
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Box display="flex" alignItems="center">
                      <CheckBoxComponent
                        labelid="rehireEligibleText"
                        defaultlabelid="Rehire"
                        ischecked={values.isRehireChecked ?? false}
                        disabled={!hasTierRangeAdjustmentEditPermission}
                        CheckHandleChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFieldValue(
                            'isRehireChecked',
                            event.target.checked,
                          );
                          if (!event.target.checked) {
                            setFieldValue('rehireMonths', null);
                          }
                        }}
                          
                      />
                      <ActionToolTip
                        labelId="rehireEligibleTooltipText"
                        defaultLabel="You can enter the number of months in the text field, and the Tier Range can be adjusted accordingly in TBB."
                      >
                        <InfoIcon
                          className="ml-xs"
                          style={{
                            cursor: 'pointer',
                            flexShrink: 0,
                            marginLeft: 0,
                            pointerEvents:
                              isSuperAdmin &&
                              hasTierRangeAdjustmentEditPermission
                                ? 'auto'
                                : 'none',
                          }}
                        />
                      </ActionToolTip>
                      {values.isRehireChecked && (
                        <Box display="flex" flexDirection="column">
                          <Textinput
                            name="rehireMonths"
                            labelid="rehireMonthsText"
                            defaultlabelid="Months"
                            Required
                            Value={values.rehireMonths?.toString() ?? ''}
                            type="numeric"
                            inputProps={{ maxLength: 3 }}
                            handlechange={(value: string) => {
                              const clean = value.replace(/\D/g, '');
                              if (clean === '') {
                                setFieldValue('rehireMonths', null);
                                return;
                              }
                              const numericValue = parseInt(clean, 10);
                              if (numericValue === 0 || numericValue > 999) return;
                              setFieldValue('rehireMonths', numericValue);
                            }}
                            className="bg__white ml-sm"
                            disabled={
                          
                              !hasTierRangeAdjustmentEditPermission
                            }
                          />
                          {errors.rehireMonths && (
                            <MediumTypography
                              className="errorText-md ml-sm"
                              textstyle="left"
                              labelid={errors.rehireMonths}
                              defaultlabel="Months field is required."
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item lg={10}>
                    <MediumTypography
                      textColor="#2A4241"
                      fontweight={600}
                      labelid="employeeCredentialsText"
                      defaultlabel="Employee Discipline"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <DropdownComponent
                      names={disciplineList}
                      labelid="EmployeeScreen.DisciplineText"
                      defaultlabelid="Discipline"
                      value={
                        values.disciplineHistoryList &&
                        values.disciplineHistoryList.length > 0 &&
                        values.disciplineHistoryList[0].disciplineType
                          ? values.disciplineHistoryList[0].disciplineType
                          : ''
                      }
                      handleChange={(e) => {
                        formikRef.current?.setFieldValue(
                          `disciplineHistoryList.${0}.isChanged`,
                          true,
                        );
                        formikRef.current?.setFieldValue(
                          `disciplineHistoryList.${0}.disciplineType`,
                          e,
                        );
                        if (
                          values.employeeStartDate !== undefined ||
                          values.employeeStartDate !== null
                        ) {
                          formikRef.current?.setFieldValue(
                            `disciplineHistoryList.${0}.startDate`,
                            values.employeeStartDate,
                          );
                        }
                      }}
                      Required
                    />
                    {Array.isArray(errors.disciplineHistoryList) &&
                      errors.disciplineHistoryList.length > 0 &&
                      (
                        errors.disciplineHistoryList[0] as
                          | FormikErrors<EmployeeDisciplineHistory>
                          | undefined
                      )?.disciplineType &&
                      Array.isArray(touched.disciplineHistoryList) &&
                      touched.disciplineHistoryList.length > 0 &&
                      (
                        touched.disciplineHistoryList[0] as
                          | {
                              disciplineType?: unknown;
                            }
                          | undefined
                      )?.disciplineType !== undefined && (
                        <MediumTypography
                          className="errorText-md"
                          labelid={
                            (
                              errors.disciplineHistoryList[0] as
                                | FormikErrors<EmployeeDisciplineHistory>
                                | undefined
                            )?.disciplineType as string
                          }
                          defaultlabel="Discipline Type is required"
                        />
                      )}
                    {typeof errors.disciplineHistoryList === 'string' &&
                      touched.disciplineHistoryList && (
                        <MediumTypography
                          className="errorText-md"
                          labelid={'discipline.fieldEmptyMessage'}
                          defaultlabel="Discipline Type is required"
                        />
                      )}
                  </Grid>
                  <Grid item xs={4}>
                    <Textinput
                      className="bg__white"
                      name="credentialLicenceNum"
                      labelid="employee.credentialLicenceNum"
                      defaultlabelid="License Number"
                      Value={
                        values.disciplineHistoryList &&
                        values.disciplineHistoryList.length > 0 &&
                        values.disciplineHistoryList[0].credentialLicenseNumber
                          ? values.disciplineHistoryList[0]
                              .credentialLicenseNumber
                          : ''
                      }
                      inputProps={{
                        maxLength: 20,
                      }}
                      handlechange={(text) => {
                        const validatedValue = licenseNumValidation(text);
                        if (validatedValue !== undefined) {
                          formikRef.current?.setFieldValue(
                            `disciplineHistoryList.${0}.isChanged`,
                            true,
                          );
                          formikRef.current?.setFieldValue(
                            `disciplineHistoryList.${0}.credentialLicenseNumber`,
                            validatedValue,
                          );
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <DatePickerComponent
                      className="bg__white"
                      labelid="expDate"
                      defaultlabelid="Expiration Date"
                      value={
                        values.disciplineHistoryList &&
                        values.disciplineHistoryList.length > 0 &&
                        values.disciplineHistoryList[0].credentialExpirationDate
                          ? dayjs(
                              values.disciplineHistoryList[0]
                                .credentialExpirationDate,
                            )
                          : null
                      }
                      handlechange={(date: Dayjs | null) => {
                        formikRef.current?.setFieldValue(
                          `disciplineHistoryList.${0}.isChanged`,
                          true,
                        );
                        if (date === null) {
                          formikRef.current?.setFieldValue(
                            `disciplineHistoryList.${0}.credentialExpirationDate`,
                            null,
                          );
                        } else {
                          formikRef.current?.setFieldValue(
                            `disciplineHistoryList.${0}.credentialExpirationDate`,
                            dayjs(date).format(DATE_FORMAT),
                          );
                        }
                      }}
                    />
                    {errors.employeeCredentials?.credentialExpirationDate &&
                      touched.employeeCredentials?.credentialExpirationDate && (
                        <MediumTypography
                          className="errorText-md"
                          labelid={
                            errors.employeeCredentials?.credentialExpirationDate
                          }
                          defaultlabel="Field should not be empty"
                        />
                      )}
                  </Grid>
                  <Grid item xs={4}>
                    <Textinput
                      className="bg__white"
                      labelid="npiNumberText"
                      defaultlabelid="NPI Number"
                      inputProps={{
                        maxLength: 10,
                      }}
                      Required={values.disciplineType !== 'NA'}
                      Value={values.npi}
                      handlechange={(value: string) => {
                        setFieldValue('npi', value);
                      }}
                    />
                    {errors.npi && touched.npi && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.npi}
                        defaultlabel="NPI Number is required"
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
              <Box>
                <Grid
                  className="flex__ justifyContent-FlexEnd pt-lg "
                  container
                  direction="row"
                  alignItems="right"
                >
                  <Grid item>
                    <ButtonComponent
                      className="btn-primary btn-cancel  mr-md"
                      variantType="outlined"
                      labelId="cancelText"
                      defaultLabelId="Cancel"
                      onClick={() => {
                        navigate(EMPLOYEES_ROUTE_NAME);
                      }}
                    />

                    <ButtonComponent
                      className="btn-primary btn-submit"
                      variantType="contained"
                      labelId={'saveText'}
                      defaultLabelId="Save"
                      onClick={() => {
                        console.log(
                          formikRef.current?.errors,
                          'errors',
                          errors,
                          localSelectedProgramId,
                        );
                        handleSubmit();
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
          </Dialog>
        )}
      </Formik>
    </Box>
  );
};

export default OnBoardingEmployeeModal;
